import { Add } from '@mui/icons-material';
import RemoveIcon from '@mui/icons-material/Remove';
import { Button, Divider, IconButton, InputAdornment, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useFormikContext } from 'formik';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { CurrencyFieldText } from 'src/components/mui-overrides/Currency';
import { DefaultTextField } from 'src/components/mui-overrides/Textfield';
import { LaunchpadCard } from 'src/components/ui/LaunchpadCard';
import { TwoColumnGridContainer } from 'src/components/ui/TwoColumnGridContainer';
import { Iasset2LangValue } from 'src/models';
import { POST_WITH_ID } from '@brickwise/asset-validation/dist/asset/assetData';
import { EditableLabel } from './EditableLabel';
import { AssetRentalInformation } from './RentalInformation';
import { AssetTypeConditional } from 'src/components/user/AssetTypeConditional';
import { AssetTypeComponentSelector } from 'src/components/user/AssetTypeComponentSelector';
import { RentalSurplus } from './RentalSurplus';
import { MonthlyDistributionDateSelector } from '../atomic-components/MonthlyDistributionDateSelector';
import { RentalIncomeField } from '../atomic-components/RentalIncomeField';
import { PlannedFirstRentalIncomeDatePicker } from '../atomic-components/PlannedFirstRentalIncomeDatePicker';
import { RentalDetailsTextField } from '../atomic-components/RentalDetailsTextField';
import { PayoutIntervalSelector } from '../atomic-components/PayoutIntervalSelector';
import { RevenuePoolAutocompleteField } from '../atomic-components/RevenuePoolAutocompleteField';
import Box from '@mui/material/Box';
import { RentalReturnField } from '../atomic-components/RentalReturnField';

export const initialNomenclature = {
  rentalIncomeTitle: [] as Iasset2LangValue[],
  rentalIncome: [] as Iasset2LangValue[],
  managementFee: [] as Iasset2LangValue[],
  liquidityReserve: [] as Iasset2LangValue[],
  total: [] as Iasset2LangValue[],
  expectedYieldDisclaimer: [] as Iasset2LangValue[],
};

interface Props {
  disabled: boolean;
}

export const AssetRentalIncome: FC<Props> = ({ disabled }) => {
  const { t } = useTranslation();
  const formik = useFormikContext<POST_WITH_ID>();

  const addClicked = () => {
    const currentAdditionalCosts = formik.values.rentalDetails?.additionalManagementCosts || [];
    formik.setFieldValue('rentalDetails.additionalManagementCosts', [...currentAdditionalCosts, { name: '', amount: '' }]);
  };

  const removeClicked = (index: number) => {
    const currentAdditionalCosts = formik.values.rentalDetails?.additionalManagementCosts || [];
    currentAdditionalCosts.splice(index, 1);
    formik.setFieldValue('rentalDetails.additionalManagementCosts', currentAdditionalCosts);
  };

  const currentAdditionalCosts = formik.values.rentalDetails?.additionalManagementCosts || [];

  return (
    <LaunchpadCard>
      {/* We shouldn't render a TwoColumnGridContainer when the asset type is powerplant */}
      <AssetTypeComponentSelector IfPowerplant={React.Fragment} IfRealEstate={TwoColumnGridContainer} IfPlot={TwoColumnGridContainer}>
        <AssetTypeConditional supported={['realEstate', 'builderOwnerModel', 'plot']}>
          <AssetRentalInformation disabled={false} />
        </AssetTypeConditional>
        <div>

        <AssetTypeConditional supported={['realEstate', 'builderOwnerModel', 'powerplant', 'powerplantPortfolio', 'plot']}>
          <Typography gutterBottom variant="h6">
            {t('dashboard.assets.addedit.main.rentalincome.text.rentaldescription')}
          </Typography>
          <RentalDetailsTextField />
          <AssetTypeConditional supported={['powerplantPortfolio']}>
            <Box sx={{ mb: 2 }}>
              <RevenuePoolAutocompleteField t={t} />
            </Box>
          </AssetTypeConditional>
          <EditableLabel
            nomenclatureField="rentalIncomeTitle"
            fallbackTranslationKey={'dashboard.assets.addedit.main.rentalincome.text.income'}
            longtext={false}
            size="h6"
            disabled={disabled}
          />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              columnGap: '24px',
            }}
          >
            <Grid sx={{ display: 'flex', alignItems: 'center' }}>
              <EditableLabel
                nomenclatureField="rentalIncome"
                fallbackTranslationKey={'dashboard.assets.addedit.main.rentalincome.text.income'}
                longtext={false}
                disabled={disabled}
              />
            </Grid>
            <RentalIncomeField disabled={disabled} />
            <Grid sx={{ display: 'flex', alignItems: 'center' }}>
              <EditableLabel
                nomenclatureField="managementFee"
                fallbackTranslationKey={'dashboard.assets.addedit.main.rentalincome.text.managementfee'}
              />
            </Grid>
            <CurrencyFieldText
              disabled={disabled}
              hideHelperText={true}
              size="small"
              label={t('dashboard.assets.addedit.main.rentalincome.fields.managementfee')}
              fieldName="rentalDetails.managementFee"
              formik={formik}
              currency={'€'}
            />
            <Grid sx={{ display: 'flex', alignItems: 'center' }}>
              -{' '}
              <EditableLabel
                nomenclatureField="liquidityReserve"
                fallbackTranslationKey={'dashboard.assets.addedit.main.rentalincome.text.liquidityreserve'}
                longtext={false}
                disabled={disabled}
              />
              :
            </Grid>
            <CurrencyFieldText
              disabled={disabled}
              hideHelperText={true}
              size="small"
              label={t('dashboard.assets.addedit.main.rentalincome.fields.liquidityreserve')}
              fieldName="rentalDetails.liquidityReserve"
              formik={formik}
              currency={'€'}
            />
          </div>
          <div style={{ paddingBottom: '24px' }}>
            {currentAdditionalCosts.map((expense, index) => {
              return (
                <div
                  key={index}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    columnGap: '24px',
                  }}
                >
                  <DefaultTextField
                    disabled={disabled}
                    label={t('dashboard.assets.addedit.main.rentalincome.fields.otherexpenses')}
                    fieldName={`rentalDetails.additionalManagementCosts.${index}.name`}
                    fieldList={['rentalDetails.additionalManagementCosts', index.toString(), 'name']}
                    formik={formik}
                    size="small"
                    hideHelperText={true}
                    muiInputProps={{
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          onClick={() => {
                            removeClicked(index);
                          }}
                        >
                          <IconButton edge="end">{<RemoveIcon />}</IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <CurrencyFieldText
                    disabled={disabled}
                    hideHelperText={true}
                    size="small"
                    label={expense.name || ''}
                    fieldName={`rentalDetails.additionalManagementCosts.${index}.amount`}
                    fieldList={['rentalDetails.additionalManagementCosts', index.toString(), 'amount']}
                    formik={formik}
                    currency={'€'}
                  />
                </div>
              );
            })}
          </div>
          <div>
            <Button disabled={disabled} variant="contained" startIcon={<Add />} onClick={addClicked}>
              {t('dashboard.assets.addedit.main.rentalincome.button.addexpense')}
            </Button>
          </div>
          <Divider sx={{ marginTop: 2, marginBottom: 4 }} />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr',
              alignItems: 'center',
            }}
          >
            <EditableLabel
              nomenclatureField="total"
              fallbackTranslationKey={'dashboard.assets.addedit.main.rentalincome.text.outcome'}
              longtext={false}
              disabled={disabled}
            />
            <RentalSurplus />
          </div>
          </AssetTypeConditional>

          <AssetTypeConditional supported={['citizenParticipationLoan']}>
            <RentalReturnField disabled={false} />
          </AssetTypeConditional>

          <PayoutIntervalSelector disabled={disabled} />
          <MonthlyDistributionDateSelector disabled={disabled} />
          <PlannedFirstRentalIncomeDatePicker disabled={disabled} />
          <EditableLabel
            nomenclatureField="expectedYieldDisclaimer"
            fallbackTranslationKey={'dashboard.assets.addedit.main.rentalincome.text.expectedYieldDisclaimer'}
            longtext={true}
            disabled={disabled}
          />
        </div>
      </AssetTypeComponentSelector>
    </LaunchpadCard>
  );
};
